/**
 * Utility functions for handling RESTHeart document IDs of various types
 */

/**
 * Formats a document ID for display, handling various RESTHeart ID types
 * @param id The document ID (can be string, number, object, etc.)
 * @returns Formatted string representation of the ID
 */
export function formatId(id: any): string {
  if (typeof id === 'object' && id !== null) {
    // Same extended-JSON shapes buildDocumentUrl already understands — show
    // the readable value instead of dumping the raw {"$oid": "..."} object.
    if (id.$oid) return id.$oid;
    if (id.$date) return new Date(id.$date).toLocaleString();
    if (id.$minKey) return 'MinKey';
    if (id.$maxKey) return 'MaxKey';
    return JSON.stringify(id);
  }
  return String(id);
}

/**
 * Builds a document URL with proper ID encoding and type parameter
 * @param baseUrl The base URL (e.g., "https://api.example.com/collection")
 * @param docId The document ID
 * @returns Complete URL with encoded ID and id_type parameter if needed
 */
export function buildDocumentUrl(baseUrl: string, docId: any): string {
  // Determine ID value and type based on RESTHeart document ID types
  let idValue: string;
  let idType = '';

  if (typeof docId === 'object' && docId !== null) {
    if (docId.$oid) {
      // ObjectId: extract the hex string (default STRING_OID)
      idValue = docId.$oid;
    } else if (docId.$date) {
      // Date type
      idValue = docId.$date;
      idType = '?id_type=DATE';
    } else if (docId.$minKey) {
      // MinKey type
      idValue = 'minkey';
      idType = '?id_type=MINKEY';
    } else if (docId.$maxKey) {
      // MaxKey type
      idValue = 'maxkey';
      idType = '?id_type=MAXKEY';
    } else {
      // Generic object - stringify
      idValue = JSON.stringify(docId);
    }
  } else if (typeof docId === 'number') {
    // Number type
    idValue = docId.toString();
    idType = '?id_type=NUMBER';
  } else if (typeof docId === 'boolean') {
    // Boolean type
    idValue = docId.toString();
    idType = '?id_type=BOOLEAN';
  } else if (docId === null) {
    // Null type
    idValue = 'null';
    idType = '?id_type=NULL';
  } else {
    // String type (default STRING_OID - auto-detects ObjectId hex format)
    idValue = String(docId);
  }

  // URL encode the ID value
  const encodedId = encodeURIComponent(idValue);

  // Ensure baseUrl doesn't end with /
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  return `${cleanBaseUrl}/${encodedId}${idType}`;
}
